import { Download, MicOff, Pause, Play, Square } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { ErrorState, Spinner } from "@/components/ui/feedback";
import { CallTranscript } from "@/features/call-transcript";
import { ApiError, fetchFile, saveBlob } from "@/lib/api";
import { useCallRecording } from "@/lib/queries";
import { fileSize, seekAt, seekByKey } from "@/lib/recording-playback";
import { transcriptStamp } from "@/lib/transcripts";
import type { Recording } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * A call's recording and its transcript, as one block: a transport over the words.
 *
 * **Why one block and not a player with a transcript under it.** They are two views of one
 * thing, and the motion this exists for — read down, spot something, hear it — crosses between
 * them constantly. The browser's own player drew a grey pill that belonged to no screen, the
 * words sat in a box of their own below it, and a reader had to match the two by eye. Here the
 * transport is the block's first row, the file's facts and the transcript's controls its second,
 * and the lines follow, with the one being spoken marked and kept in view.
 *
 * **Why the audio is fetched instead of linked.** `audio_url` is a path on core-api that
 * requires the session, and the access token lives in a module variable and rides in an
 * `Authorization` header (see `lib/api.ts`). An `<audio src>` issues a plain GET with no
 * headers, so it would 401. Fetching the bytes through the API client and handing the element
 * an object URL is the only way to keep playback inside the session — and keeping it there is
 * the point: the server proxies from the bucket rather than handing out a pre-signed link
 * precisely so a URL that leaks is not a conversation that leaks. The cost is that the whole
 * file is loaded before playback starts, so scrubbing is instant but the first press waits.
 *
 * **Nothing is fetched until asked.** Opening a call must not download a recording nobody asked
 * to hear. The first press — Play, a transcript line, the bar — pulls the file and then does what
 * it was asked, which is what `pending` carries between the press and the element's
 * `loadedmetadata`. A line clicked before the file is loaded is therefore not a dead control:
 * it loads, seeks and plays.
 *
 * **The element is hand-driven, not `controls`.** Play, Pause, Stop and the bar are the
 * console's own so that they sit in the dialog's type and colour, and so the transcript can
 * seek the same element the bar does. It stays keyboard-accessible: the buttons are buttons
 * and the bar is a slider (`role="slider"`, arrows and Home/End).
 *
 * `second` is the playhead rounded down before it becomes state: `timeupdate` fires about four
 * times a second, and the transcript's highlight can only move when a line boundary is crossed,
 * so the whole list re-renders at 1 Hz rather than 4. The bar keeps its own finer clock.
 */
export function CallAudio({
  callId,
  hasTranscript,
}: {
  callId: string | undefined;
  /** The call row's `has_transcript`: a call with a transcript and no recording still has lines
   *  to read, and the panel asks for them only when the row or the recording says to. */
  hasTranscript: boolean;
}) {
  const { data: recording, isPending, error } = useCallRecording(callId);
  const [source, setSource] = React.useState<{ url: string; blob: Blob } | null>(null);
  const [audio, setAudio] = React.useState<HTMLAudioElement | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [playing, setPlaying] = React.useState(false);
  const [second, setSecond] = React.useState(0);
  // Whether the transcript scrolls to keep the line being spoken in view. Off once the reader
  // scrolls it themselves; back on with any press that moves the playhead, since that is them
  // asking to be taken somewhere.
  const [following, setFollowing] = React.useState(true);
  // What the press that fetched the file wants done with it once it can play. A ref because it
  // is a message from one event to another, not something to render.
  const pending = React.useRef<{ at: number | null; play: boolean } | null>(null);
  const fetching = React.useRef(false);

  // Object URLs are a leak if nothing revokes them, and this unmounts every time the dialog
  // closes.
  React.useEffect(() => () => {
    if (source) URL.revokeObjectURL(source.url);
  }, [source]);

  const fetchBlob = React.useCallback(async (): Promise<Blob | null> => {
    if (!recording) return null;
    if (source) return source.blob;
    if (fetching.current) return null;
    fetching.current = true;
    setLoading(true);
    setLoadError(null);
    try {
      const { blob } = await fetchFile(new URL(recording.audio_url).pathname);
      setSource({ url: URL.createObjectURL(blob), blob });
      return blob;
    } catch (e) {
      pending.current = null;
      setLoadError(e instanceof ApiError ? e.message : "Could not load the audio for this call.");
      return null;
    } finally {
      fetching.current = false;
      setLoading(false);
    }
  }, [recording, source]);

  /** Play from `at` (or from where it is): now if the element exists, once it does otherwise. */
  const playFrom = React.useCallback(
    (at: number | null) => {
      setFollowing(true);
      if (audio) {
        if (at !== null) {
          audio.currentTime = at;
          setSecond(Math.floor(at));
        }
        void audio.play().catch(() => {
          // Autoplay policies, a file that failed to decode. The seek still landed, and the
          // reader has a real transport right here to press.
        });
        return;
      }
      pending.current = { at, play: true };
      void fetchBlob();
    },
    [audio, fetchBlob],
  );

  const seek = React.useCallback((seconds: number) => playFrom(seconds), [playFrom]);

  function stop() {
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setSecond(0);
    setFollowing(true);
  }

  async function download() {
    if (!recording) return;
    const blob = await fetchBlob();
    if (blob) saveBlob(blob, `${recording.call_id}.wav`);
  }

  if (isPending) return <Spinner className="size-4" />;
  if (error) return <ErrorState error={error} title="Could not check for a recording" />;
  if (!recording && !hasTranscript) return <NoRecording />;

  return (
    <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-surface">
      {source && (
        <audio
          ref={setAudio}
          src={source.url}
          preload="auto"
          className="hidden"
          onLoadedMetadata={(e) => {
            const then = pending.current;
            pending.current = null;
            if (!then) return;
            if (then.at !== null) e.currentTarget.currentTime = then.at;
            if (then.play) void e.currentTarget.play().catch(() => {});
          }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onTimeUpdate={(e) => setSecond(Math.floor(e.currentTarget.currentTime))}
        />
      )}
      {recording && (
        <Transport
          audio={audio}
          length={recording.duration_ms ? recording.duration_ms / 1000 : 0}
          loading={loading}
          playing={playing}
          onPlay={() => playFrom(null)}
          onPause={() => audio?.pause()}
          onStop={stop}
          onSeek={seek}
        />
      )}
      <CallTranscript
        callId={callId}
        hasTranscript={hasTranscript}
        meta={
          recording ? (
            <RecordingMeta recording={recording} loadError={loadError} onDownload={() => void download()} />
          ) : (
            <NoRecording />
          )
        }
        onSeek={recording ? seek : undefined}
        currentSeconds={audio ? second : undefined}
        following={following}
        onFollowingChange={setFollowing}
      />
    </div>
  );
}

function NoRecording() {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-text-subtle">
      <MicOff className="size-3.5" aria-hidden />
      {/* Three very different situations look identical from here — recording is off for this
          phone, the call was never answered, or the node has not shipped the file yet — and
          this component cannot tell them apart, so it does not pretend to. */}
      No recording
    </span>
  );
}

/** The file's facts, in the block's second row beside the transcript's controls. */
function RecordingMeta({
  recording,
  loadError,
  onDownload,
}: {
  recording: Recording;
  loadError: string | null;
  onDownload: () => void;
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-subtle">
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded hover:text-text-muted hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent-8"
        onClick={onDownload}
      >
        <Download className="size-3" aria-hidden />
        Download
      </button>
      <span>{fileSize(recording.size_bytes)}</span>
      {recording.channels === 2 && (
        // Worth surfacing: a stereo recording has each party on their own channel, which is
        // what makes an attributed transcript possible.
        <span>stereo</span>
      )}
      {loadError && <span className="text-danger-11">{loadError}</span>}
    </span>
  );
}

/**
 * Play, Pause, Stop, the bar and the clock. Owns the fine playhead (`timeupdate`, about 4 Hz)
 * so the block above it can keep to whole seconds.
 *
 * Usable before the file is loaded: the length is the recording row's, so the clock reads
 * `0:00 / 6:21` and the bar has somewhere to go, and the first press of anything fetches the
 * file. Only Stop waits for an element, having nothing to stop until then.
 */
function Transport({
  audio,
  length: known,
  loading,
  playing,
  onPlay,
  onPause,
  onStop,
  onSeek,
}: {
  audio: HTMLAudioElement | null;
  /** From the recording row, in seconds; the element's own length replaces it once loaded. */
  length: number;
  loading: boolean;
  playing: boolean;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onSeek: (seconds: number) => void;
}) {
  const [time, setTime] = React.useState(0);
  const [length, setLength] = React.useState(known);

  React.useEffect(() => {
    if (!audio) return;
    const onTime = () => setTime(audio.currentTime);
    const onLength = () => {
      if (Number.isFinite(audio.duration)) setLength(audio.duration);
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("seeked", onTime);
    audio.addEventListener("durationchange", onLength);
    onLength();
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("seeked", onTime);
      audio.removeEventListener("durationchange", onLength);
    };
  }, [audio]);

  return (
    <div className="flex items-center gap-2 px-2.5 py-2">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={playing ? "Pause" : "Play"}
        disabled={loading}
        onClick={playing ? onPause : onPlay}
      >
        {loading ? <Spinner /> : playing ? <Pause className="fill-current" /> : <Play className="fill-current" />}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Stop"
        disabled={!audio}
        onClick={onStop}
      >
        <Square className="fill-current" />
      </Button>
      <span className="w-10 text-right font-mono text-xs tabular-nums text-text">
        {transcriptStamp(time)}
      </span>
      <SeekBar
        time={time}
        length={length}
        disabled={length <= 0}
        onSeek={(seconds) => {
          setTime(seconds);
          onSeek(seconds);
        }}
      />
      <span className="w-10 font-mono text-xs tabular-nums text-text-muted">
        {transcriptStamp(length)}
      </span>
    </div>
  );
}

/**
 * The playhead as a bar: press or drag anywhere on it to seek, arrows and Home/End from the
 * keyboard. Drawn on the meter the audio-quality figures use, so the two read as one system.
 * The hit area is taller than the track, because a 6px target is not a target.
 */
function SeekBar({
  time,
  length,
  disabled,
  onSeek,
}: {
  time: number;
  length: number;
  disabled: boolean;
  onSeek: (seconds: number) => void;
}) {
  const bar = React.useRef<HTMLDivElement>(null);
  const dragging = React.useRef(false);
  const percent = length > 0 ? Math.min(100, Math.max(0, (time / length) * 100)) : 0;

  const at = (e: React.PointerEvent) => {
    const rect = bar.current!.getBoundingClientRect();
    return seekAt(e.clientX - rect.left, rect.width, length);
  };

  return (
    <div
      ref={bar}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label="Playhead"
      aria-valuemin={0}
      aria-valuemax={Math.round(length)}
      aria-valuenow={Math.round(time)}
      aria-valuetext={`${transcriptStamp(time)} of ${transcriptStamp(length)}`}
      aria-disabled={disabled || undefined}
      className={cn(
        "relative h-5 min-w-0 flex-1 touch-none rounded",
        disabled ? "cursor-default" : "cursor-pointer",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent-8",
      )}
      onPointerDown={(e) => {
        if (disabled || e.button !== 0) return;
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        onSeek(at(e));
      }}
      onPointerMove={(e) => {
        if (dragging.current) onSeek(at(e));
      }}
      onPointerUp={(e) => {
        if (!dragging.current) return;
        dragging.current = false;
        e.currentTarget.releasePointerCapture(e.pointerId);
      }}
      onPointerCancel={() => {
        dragging.current = false;
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        const next = seekByKey(e.key, time, length);
        if (next === null) return;
        e.preventDefault();
        onSeek(next);
      }}
    >
      <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 overflow-hidden rounded-full bg-gray-4">
        <div className="h-full rounded-full bg-accent-9" style={{ width: `${percent}%` }} />
      </div>
      <div
        className={cn(
          "absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent-9 shadow-sm ring-2 ring-surface",
          disabled && "opacity-0",
        )}
        style={{ left: `${percent}%` }}
        aria-hidden
      />
    </div>
  );
}
